import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';

import { IQObservation } from 'src/app/models/iq-observation.model';

@Component({
  selector: 'app-ons-widget-observations',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [DatePipe, RouterLink, MatIconModule],
  template: `
    <div
      class="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-gradient-to-br from-blue-600 via-blue-600 to-blue-700 p-6 text-white"
      aria-labelledby="ons-iq-heading"
    >
      <!-- Decorative dots -->
      <div
        class="pointer-events-none absolute inset-0 opacity-60"
        aria-hidden="true"
      >
        <span
          class="absolute top-4 right-8 h-1 w-1 rounded-full bg-white/60"
        ></span>
        <span
          class="absolute top-10 right-20 h-1 w-1 rounded-full bg-white/40"
        ></span>
        <span
          class="absolute right-28 bottom-5 h-1.5 w-1.5 rounded-full bg-white/30"
        ></span>
      </div>

      <div class="relative flex min-h-0 flex-1 flex-col">
        <!-- Header -->
        <div class="mb-4 flex gap-2">
          <mat-icon class="!h-6 !w-6 !text-lg" aria-hidden="true"
            >auto_awesome</mat-icon
          >
          <div>
            <div class="flex flex-wrap items-center gap-1.5">
              <h3
                id="ons-iq-heading"
                class="font-montserrat m-0 text-base font-bold text-white"
              >
                IQ Observations
              </h3>
              <span
                class="font-lato rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold tracking-wide text-white/70 uppercase"
              >
                AI-generated
              </span>
            </div>
            <p class="font-lato m-0 text-[10px] text-white/60">
              Review for accuracy before acting.
            </p>
          </div>
        </div>

        <!-- Body -->
        @if (isLoading()) {
          <div
            class="space-y-2 py-1"
            role="status"
            aria-label="Loading observations"
          >
            <div class="h-3 w-10/12 animate-pulse rounded bg-white/20"></div>
            <div class="h-3 w-8/12 animate-pulse rounded bg-white/20"></div>
            <div class="h-3 w-9/12 animate-pulse rounded bg-white/20"></div>
            <div
              class="mt-4 h-3 w-7/12 animate-pulse rounded bg-white/20"
            ></div>
            <div class="h-3 w-10/12 animate-pulse rounded bg-white/20"></div>
          </div>
        } @else {
          <div class="min-h-0 flex-1 overflow-y-auto pr-1">
            @if (observations()?.length) {
              @for (obs of observations(); track obs) {
                <div class="border-b border-white/10 py-3 last:border-0">
                  <div class="font-lato mb-1 text-[10px] text-white/50">
                    {{ obs.date + ' ' + obs.beginTime | date: 'short' }}
                  </div>
                  <div
                    class="font-lato text-sm leading-relaxed whitespace-pre-line text-white/90"
                  >
                    {{ obs.summary }}
                    @if (obs.caseLink) {
                      Case
                      <a
                        class="font-semibold underline decoration-white/40 underline-offset-2"
                        [routerLink]="obs.caseLink.url"
                      >
                        {{ obs.caseLink.label }}
                      </a>
                      has been opened to track progress.
                    }
                  </div>
                </div>
              }
            } @else {
              <p class="font-lato text-sm text-white/80">
                No network performance degradation detected by the IQ analytics
                engine.
              </p>
            }
          </div>
        }

        <!-- Footer -->
        <div class="mt-4 border-t border-white/10 pt-3">
          <a
            class="font-lato inline-flex items-center gap-1 text-sm font-semibold text-white underline decoration-white/40 underline-offset-2 hover:decoration-white"
            routerLink="/analytics/historical-observations"
          >
            Historical Observations
            <mat-icon
              class="h-4! w-4! align-middle! text-base! leading-none!"
              aria-hidden="true"
              >arrow_outward</mat-icon
            >
          </a>
        </div>
      </div>
    </div>
  `,
})
export class WidgetObservationsComponent {
  readonly observations = input<IQObservation[]>([]);

  readonly isLoading = input<boolean>(false);
}
